import React, { useState, useEffect } from 'react';
import styles from './category-box.module.scss';
import {
  ItemChecklistIcon,
  DirectoryChecklistIcon,
  DirectoryCompendiumIcon,
  DirectoryKnowledgeIcon,
  ItemNumberIcon,
  ItemBinderIcon,
  ItemCompendiumIcon,
  MediaNumberIcon,
} from '../../../images';
import { Input } from 'antd';
import { TagIcon } from '../../tags/tagsIcons/TagIcon.component';
import { TextTag } from '../../tags/text-tags';
import { UpdatedIndication } from '../../updatedIndication';
import { CATEGORY_TYPES } from 'shared/constants/knowledge.constants';
import classnames from 'classnames';
import _ from 'lodash';
import { LabelsList } from '../../labels';
const { TextArea } = Input;

const CATEGORY_TYPE = {
  [CATEGORY_TYPES.KNOWLEDGE]: {
    icon: ItemBinderIcon,
    folderIcon: DirectoryKnowledgeIcon,
  },
  [CATEGORY_TYPES.CHECKS]: {
    icon: ItemChecklistIcon,
    iconStyle: styles.checklistIcon,
    folderIcon: DirectoryChecklistIcon,
  },
  [CATEGORY_TYPES.VALUES]: {
    icon: ItemCompendiumIcon,
    iconStyle: styles.labIcon,
    folderIcon: DirectoryCompendiumIcon,
  },
};

const CategoryBoxComponent = React.memo(
  ({
    title,
    type,
    tags,
    adminMode,
    onBlur,
    onClick,
    actionButtons,
    lastUpdatedAt,
    categoryType,
    metadata,
    labels,
  }) => {
    const { icon = null, iconStyle = null } = CATEGORY_TYPE[type] ?? {};
    const isCategory = type === CATEGORY_TYPES.CATEGORY;
    const [titleInput, setTitleInput] = useState(title);

    useEffect(() => {
      if (titleInput !== title) {
        setTitleInput(title);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, setTitleInput]);

    const onInputFocus = React.useCallback(e => {
      e.stopPropagation();
    }, []);

    const onHeaderPress = React.useCallback(e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.target.blur();
      }
    }, []);

    const onChange = React.useCallback(
      e => {
        const { value } = e.target;
        setTitleInput(value);
      },
      [setTitleInput],
    );

    const renderIcon = () => {
      if (isCategory) {
        const FolderIcon = CATEGORY_TYPE[categoryType]?.folderIcon || DirectoryKnowledgeIcon;
        return <img src={FolderIcon} className={styles.icon} alt={categoryType} />;
      }

      return <img src={icon} alt={title} className={classnames(styles.icon, iconStyle)} />;
    };

    const renderIconTags = () => (
      <div className={styles.tags}>
        {tags?.specialty && <TagIcon tag={tags.specialty} />}
        {tags?.subspecialty && <TagIcon tag={tags.subspecialty} />}
      </div>
    );
    const renderMetadata = () => {
      if (_.isEmpty(metadata) || isCategory) return;

      const hasItems = metadata?.items > 0;
      const hasMedia = metadata?.media > 0;
      const showMetadata = hasItems || hasMedia;

      const showItems = !adminMode ? hasItems : true;
      const showMedia = !adminMode ? hasMedia : true;

      if (!adminMode && !showMetadata) return;

      return (
        <div className={styles.metadata}>
          {showItems && (
            <div className={styles.metadataContainer}>
              <img src={ItemNumberIcon} alt={'number of items'} className={styles.metadataIcon} />
              <h5 className={styles.metadataText}>{metadata.items}</h5>
            </div>
          )}
          {showMedia && (
            <div className={styles.metadataContainer}>
              <img src={MediaNumberIcon} alt={'number of items'} className={styles.metadataIcon} />
              <h5 className={styles.metadataText}>{metadata.media}</h5>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className={styles.container} onClick={onClick}>
        {actionButtons()}
        <div className={styles.header}>
          {renderIcon()}
          <div className={styles.inline}>
            <TextArea
              onClick={onInputFocus}
              onKeyPress={onHeaderPress}
              onBlur={onBlur}
              className={styles.text}
              autoSize={{ minRows: 2, maxRows: 2 }}
              disabled={!adminMode}
              onChange={onChange}
              value={titleInput}
            />
            {!adminMode && lastUpdatedAt && (
              <div className={styles.updated}>
                <UpdatedIndication lastUpdate={lastUpdatedAt} />
              </div>
            )}
          </div>
          <div className={styles.labels}>
            <TextTag tag={tags?.contentType} className={styles.contentTypeLabel} />
            <LabelsList labels={labels} className={styles.labelsItems} />
          </div>
        </div>
        <div className={styles.footer}>
          {renderIconTags()}
          {renderMetadata()}
        </div>
      </div>
    );
  },
);

CategoryBoxComponent.defaultProps = {
  title: '',
  type: CATEGORY_TYPES.CATEGORY,
  tags: {},
  adminMode: false,
  onBlur: () => {},
  onClick: () => {},
  actionButtons: () => {},
  lastUpdatedAt: null,
  categoryType: null,
  metadata: {},
};

export { CategoryBoxComponent };
